import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { secondaryServices, companyInfo } from '../data';
import { CheckCircle2, ArrowLeft, Phone, Sparkles, ShieldCheck, Clock } from 'lucide-react';
import { SimpleContactForm } from '../components/ContactForm';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = secondaryServices.find(s => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4 text-slate-900">Service ikke fundet</h1>
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Gå tilbage til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Dynamic Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-slate-950">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 opacity-50">
          <img 
            src={service.image} 
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8 group bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/10"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">Tilbage til forsiden</span>
            </Link>
            
            <div className="flex items-center gap-2 text-slate-400 mb-4">
              <Sparkles className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-bold tracking-widest uppercase">Premium Detailing Service</span>
            </div>
            
            <h1 className="text-4xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              {service.title}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed max-w-2xl">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content & Visuals */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left side: Information */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Enestående Resultater</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  {service.longDescription}
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100 group hover:border-slate-300 transition-colors">
                      <CheckCircle2 className="w-5 h-5 text-slate-500 shrink-0" />
                      <span className="text-slate-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call to Action Box */}
              <div className="bg-slate-900 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <ShieldCheck className="w-32 h-32" />
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold mb-4">Klar til en bil der står som ny?</h3>
                  <p className="text-slate-400 mb-8 max-w-md">
                    Vi leverer altid den højeste kvalitet. Kontakt os i dag for en uforpligtende snak om din bil.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a 
                      href={`tel:${companyInfo.phone}`}
                      className="flex items-center justify-center gap-3 bg-slate-500 hover:bg-slate-400 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-slate-500/20"
                    >
                      <Phone className="w-5 h-5" />
                      Ring på {companyInfo.phone}
                    </a>
                    <a 
                      href="#contact"
                      className="flex items-center justify-center px-8 py-4 rounded-2xl font-bold border border-white/20 hover:bg-white/10 transition-all"
                    >
                      Bestil et tilbud
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side: Imagery & Badges */}
            <div className="lg:sticky lg:top-32 space-y-8">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-slate-100">
                  <img 
                    src={service.secondaryImage || service.image} 
                    alt={`${service.title} i aktion`}
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                  />
                </div>
                
                {/* Floating Info Cards */}
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 max-w-[260px] hidden md:block">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Hurtig Service</p>
                      <p className="text-sm font-bold text-slate-900">Book indenfor 48 timer</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Vi bestræber os på altid at finde en tid der passer dig bedst muligt.</p>
                </div>
              </div>

              {/* Small Detail Image Grid or Gallery could go here */}
              <div className="grid grid-cols-2 gap-4">
                <div className="h-48 rounded-2xl overflow-hidden shadow-lg">
                  <img src={service.image} className="w-full h-full object-cover" alt="Detail 1" />
                </div>
                <div className="h-48 rounded-2xl overflow-hidden shadow-lg bg-slate-900 flex flex-col items-center justify-center text-center p-4">
                  <Sparkles className="w-8 h-8 text-slate-500 mb-2" />
                  <p className="text-white font-bold text-sm">Garanteret Tilfredshed</p>
                  <p className="text-slate-400 text-xs mt-1">Vi stopper ikke før resultatet er perfekt.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <div id="contact" className="bg-slate-50 py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-tight">Book din {service.title} nu</h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">Udfyld formularen, så vender vi tilbage med et prisoverslag indenfor 2 timer.</p>
          </div>
          <div className="">
            <SimpleContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}
